import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface UseWebSocketProps {
    onBookingCreate?: (data: any) => void;
    onBookingUpdate?: (data: any) => void;
    onConnect?: () => void;
    onDisconnect?: () => void;
    onError?: (error: Error) => void;
}

interface UseWebSocketReturn {
    connect: () => void;
    disconnect: () => void;
    emit: (
        event: string,
        data: any,
        callback?: (response: any) => void
    ) => void;
    isConnected: boolean;
}

export const useWebSocket = ({
    onBookingCreate,
    onBookingUpdate,
    onConnect,
    onDisconnect,
    onError,
}: UseWebSocketProps): UseWebSocketReturn => {
    const ws = useRef<Socket | null>(null);
    const isConnected = useRef(false);

    const connect = useCallback(() => {
        if (ws.current?.connected) {
            console.log('WebSocket already connected');
            return;
        }

        try {
            ws.current = io(process.env.REACT_APP_SOCKET_SERVER_URL, {
                autoConnect: false,
                transports: ['websocket'],
                secure: false,
                auth: {
                    token: localStorage.getItem('accessToken'),
                },
            });

            ws.current.on('connect', () => {
                console.log('WebSocket connection established');
                isConnected.current = true;
                onConnect?.();
            });

            ws.current.on('disconnect', () => {
                console.log('WebSocket connection closed');
                isConnected.current = false;
                onDisconnect?.();
            });

            ws.current.on('connect_error', (error: Error) => {
                console.error('WebSocket connection error:', error);
                onError?.(error);
            });

            ws.current.on('booking-create', (data: any) => {
                console.log('booking-create event:', data);
                onBookingCreate?.(data);
            });

            ws.current.on('booking-update', (data: any) => {
                console.log('booking-update event:', data);
                onBookingUpdate?.(data);
            });

            ws.current.connect();

            ws.current.emit('join', 1, (res: any) => {
                console.log('Joined room 1:', res);
            });

            ws.current.emit('join', 30, (res: any) => {
                console.log('Joined room 30:', res);
            });
        } catch (error) {
            console.error('Failed to initialize WebSocket:', error);
            onError?.(error as Error);
        }
    }, [onBookingCreate, onBookingUpdate, onConnect, onDisconnect, onError]);

    const disconnect = useCallback(() => {
        if (ws.current) {
            ws.current.close();
            ws.current = null;
            isConnected.current = false;
            console.log('WebSocket disconnected');
        }
    }, []);

    const emit = useCallback(
        (event: string, data: any, callback?: (response: any) => void) => {
            if (ws.current?.connected) {
                ws.current.emit(event, data, callback);
            } else {
                console.warn(
                    'WebSocket not connected, cannot emit event:',
                    event
                );
            }
        },
        []
    );

    useEffect(() => {
        connect();

        return () => {
            disconnect();
        };
    }, [connect, disconnect]);

    return {
        connect,
        disconnect,
        emit,
        isConnected: isConnected.current,
    };
};
